import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  HelpCircle, 
  DollarSign, 
  Target, 
  TrendingDown, 
  Percent, 
  PiggyBank, 
  TrendingUp, 
  Shield, 
  Wallet, 
  Receipt, 
  ArrowDownCircle, 
  ArrowUpCircle, 
  Banknote,
  Calculator,
  BookOpen
} from 'lucide-react';

const Help = () => {
  const { t } = useTranslation();

  const dashboardMetrics = [
    {
      titleKey: "help.metrics.totalSoldValue.title",
      formulaKey: "help.metrics.totalSoldValue.formula",
      descriptionKey: "help.metrics.totalSoldValue.description",
      exampleKey: "help.metrics.totalSoldValue.example",
      icon: DollarSign,
      color: "text-green-600",
      bgColor: "bg-green-50"
    },
    {
      titleKey: "help.metrics.estimatedCost.title",
      formulaKey: "help.metrics.estimatedCost.formula",
      descriptionKey: "help.metrics.estimatedCost.description",
      exampleKey: "help.metrics.estimatedCost.example",
      icon: Target,
      color: "text-blue-600",
      bgColor: "bg-blue-50"
    },
    {
      titleKey: "help.metrics.actualCost.title",
      formulaKey: "help.metrics.actualCost.formula",
      descriptionKey: "help.metrics.actualCost.description",
      exampleKey: "help.metrics.actualCost.example",
      icon: TrendingDown,
      color: "text-orange-600",
      bgColor: "bg-orange-50"
    },
    {
      titleKey: "help.metrics.margin.title",
      formulaKey: "help.metrics.margin.formula",
      descriptionKey: "help.metrics.margin.description",
      exampleKey: "help.metrics.margin.example",
      icon: Percent,
      color: "text-purple-600",
      bgColor: "bg-purple-50"
    }
  ];

  const profitContingencyMetrics = [
    {
      titleKey: "help.metrics.plannedProfit.title",
      formulaKey: "help.metrics.plannedProfit.formula",
      descriptionKey: "help.metrics.plannedProfit.description",
      exampleKey: "help.metrics.plannedProfit.example",
      icon: PiggyBank,
      color: "text-purple-600",
      bgColor: "bg-purple-50"
    },
    {
      titleKey: "help.metrics.estimatedProfit.title",
      formulaKey: "help.metrics.estimatedProfit.formula",
      descriptionKey: "help.metrics.estimatedProfit.description",
      detailKeys: [
        "help.metrics.estimatedProfit.detail1",
        "help.metrics.estimatedProfit.detail2"
      ],
      exampleKey: "help.metrics.estimatedProfit.example",
      icon: TrendingUp,
      color: "text-green-600",
      bgColor: "bg-green-50"
    },
    {
      titleKey: "help.metrics.plannedContingency.title",
      formulaKey: "help.metrics.plannedContingency.formula",
      descriptionKey: "help.metrics.plannedContingency.description",
      exampleKey: "help.metrics.plannedContingency.example",
      icon: Shield,
      color: "text-blue-600",
      bgColor: "bg-blue-50"
    },
    {
      titleKey: "help.metrics.usedContingency.title",
      formulaKey: "help.metrics.usedContingency.formula",
      descriptionKey: "help.metrics.usedContingency.description",
      exampleKey: "help.metrics.usedContingency.example",
      icon: Shield,
      color: "text-orange-600",
      bgColor: "bg-orange-50"
    },
    {
      titleKey: "help.metrics.remainingContingency.title",
      formulaKey: "help.metrics.remainingContingency.formula",
      descriptionKey: "help.metrics.remainingContingency.description",
      exampleKey: "help.metrics.remainingContingency.example",
      icon: Shield,
      color: "text-green-600",
      bgColor: "bg-green-50"
    }
  ];

  const cashFlowMetrics = [
    {
      titleKey: "help.metrics.totalReceivable.title",
      formulaKey: "help.metrics.totalReceivable.formula",
      descriptionKey: "help.metrics.totalReceivable.description",
      icon: Receipt,
      color: "text-blue-600",
      bgColor: "bg-blue-50"
    },
    {
      titleKey: "help.metrics.totalReceived.title",
      formulaKey: "help.metrics.totalReceived.formula",
      descriptionKey: "help.metrics.totalReceived.description",
      icon: ArrowDownCircle,
      color: "text-green-600",
      bgColor: "bg-green-50"
    },
    {
      titleKey: "help.metrics.netReceivable.title",
      formulaKey: "help.metrics.netReceivable.formula",
      descriptionKey: "help.metrics.netReceivable.description",
      icon: Receipt,
      color: "text-purple-600",
      bgColor: "bg-purple-50"
    },
    {
      titleKey: "help.metrics.totalPayable.title",
      formulaKey: "help.metrics.totalPayable.formula",
      descriptionKey: "help.metrics.totalPayable.description",
      icon: Banknote,
      color: "text-orange-600",
      bgColor: "bg-orange-50"
    },
    {
      titleKey: "help.metrics.totalPaid.title",
      formulaKey: "help.metrics.totalPaid.formula",
      descriptionKey: "help.metrics.totalPaid.description",
      icon: ArrowUpCircle,
      color: "text-red-600",
      bgColor: "bg-red-50"
    },
    {
      titleKey: "help.metrics.netPayable.title",
      formulaKey: "help.metrics.netPayable.formula",
      descriptionKey: "help.metrics.netPayable.description",
      icon: Banknote,
      color: "text-amber-600",
      bgColor: "bg-amber-50"
    },
    {
      titleKey: "help.metrics.netCashPosition.title",
      formulaKey: "help.metrics.netCashPosition.formula",
      descriptionKey: "help.metrics.netCashPosition.description",
      icon: Wallet,
      color: "text-emerald-600",
      bgColor: "bg-emerald-50"
    }
  ];

  const packageTerms = [
    { termKey: "help.terms.activePackage.term", descriptionKey: "help.terms.activePackage.description" },
    { termKey: "help.terms.completedPackage.term", descriptionKey: "help.terms.completedPackage.description" },
    { termKey: "help.terms.draftPackage.term", descriptionKey: "help.terms.draftPackage.description" },
    { termKey: "help.terms.bottomUpCosting.term", descriptionKey: "help.terms.bottomUpCosting.description" },
    { termKey: "help.terms.topDownCosting.term", descriptionKey: "help.terms.topDownCosting.description" },
    { termKey: "help.terms.variance.term", descriptionKey: "help.terms.variance.description" }
  ];

  const quickTips = [
    "help.tips.tip1",
    "help.tips.tip2",
    "help.tips.tip3",
    "help.tips.tip4",
    "help.tips.tip5"
  ];

  return (
    <div className="space-y-6" data-testid="help-page">
      <div>
        <h1 className="text-3xl font-heading font-extrabold text-foreground tracking-tight flex items-center gap-3">
          <BookOpen className="w-8 h-8 text-primary" />
          {t('help.title')}
        </h1>
        <p className="text-muted-foreground mt-2">{t('help.subtitle')}</p>
      </div>

      {/* Dashboard Main KPIs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-primary" />
            {t('help.dashboardKPIs')}
          </CardTitle>
          <CardDescription>{t('help.dashboardKPIsDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {dashboardMetrics.map((metric, idx) => {
            const Icon = metric.icon;
            return (
              <div key={idx} className={`p-4 rounded-lg ${metric.bgColor} border`}>
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-white">
                    <Icon className={`w-5 h-5 ${metric.color}`} />
                  </div>
                  <div className="flex-1">
                    <h4 className={`font-semibold text-lg ${metric.color}`}>{t(metric.titleKey)}</h4>
                    <div className="mt-2">
                      <Badge variant="outline" className="font-mono text-xs">
                        {t(metric.formulaKey)}
                      </Badge>
                    </div>
                    <p className="mt-3 text-sm text-foreground">{t(metric.descriptionKey)}</p>
                    <div className="mt-2 p-2 bg-white/70 rounded text-xs text-muted-foreground">
                      <strong>{t('help.example')}:</strong> {t(metric.exampleKey)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Profit & Contingency */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PiggyBank className="w-5 h-5 text-primary" />
            {t('help.profitContingency')}
          </CardTitle>
          <CardDescription>{t('help.profitContingencyDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {profitContingencyMetrics.map((metric, idx) => {
            const Icon = metric.icon;
            return (
              <div key={idx} className={`p-4 rounded-lg ${metric.bgColor} border`}>
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-white">
                    <Icon className={`w-5 h-5 ${metric.color}`} />
                  </div>
                  <div className="flex-1">
                    <h4 className={`font-semibold text-lg ${metric.color}`}>{t(metric.titleKey)}</h4>
                    <div className="mt-2">
                      <Badge variant="outline" className="font-mono text-xs">
                        {t(metric.formulaKey)}
                      </Badge>
                    </div>
                    <p className="mt-3 text-sm text-foreground">{t(metric.descriptionKey)}</p>
                    {metric.detailKeys && (
                      <ul className="mt-2 space-y-1">
                        {metric.detailKeys.map((detailKey, i) => (
                          <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                            <span className="text-primary">•</span>
                            {t(detailKey)}
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="mt-2 p-2 bg-white/70 rounded text-xs text-muted-foreground">
                      <strong>{t('help.example')}:</strong> {t(metric.exampleKey)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Cash Flow & Liquidity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-primary" />
            {t('help.cashFlow')}
          </CardTitle>
          <CardDescription>{t('help.cashFlowDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {cashFlowMetrics.map((metric, idx) => {
            const Icon = metric.icon;
            return (
              <div key={idx} className={`p-4 rounded-lg ${metric.bgColor} border`}>
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-white">
                    <Icon className={`w-5 h-5 ${metric.color}`} />
                  </div>
                  <div className="flex-1">
                    <h4 className={`font-semibold ${metric.color}`}>{t(metric.titleKey)}</h4>
                    <Badge variant="outline" className="font-mono text-xs mt-1">
                      {t(metric.formulaKey)}
                    </Badge>
                    <p className="mt-2 text-sm text-foreground">{t(metric.descriptionKey)}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Package Terminology */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-primary" />
            {t('help.terminology')}
          </CardTitle>
          <CardDescription>{t('help.terminologyDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            {packageTerms.map((item, idx) => (
              <div key={idx} className="p-4 border rounded-lg bg-muted/30">
                <h4 className="font-semibold text-foreground">{t(item.termKey)}</h4>
                <p className="mt-2 text-sm text-muted-foreground">{t(item.descriptionKey)}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Tips */}
      <Card className="border-primary/30 bg-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-primary">
            <HelpCircle className="w-5 h-5" />
            {t('help.quickTips')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3 text-sm">
            {quickTips.map((tipKey, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="text-primary font-bold">{idx + 1}.</span>
                <span>{t(tipKey)}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default Help;
